import React, { useEffect, useState } from 'react';
import {
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
  BubbleTypeahead,
  BubbleFileM,
} from '../BubbleUI';
import {
  Brand,
  BrandDayTheme,
  BrandFontSize,
  BrandGroupType,
  BrandImageType,
  BrandSettings,
} from '@go-shared/types/brand';
import { updateBrand, uploadBrandLogo } from '@go-client/lib/api/brand';
import { createPortal } from 'react-dom';
import { VenueRequestFile } from '@go-shared/types/groupBooking';
import { lastEl } from '@go-shared/lib/functional';
import { ObjectId } from 'bson';
import { RequiredBy } from '@go-shared/types/typeUtils';
import { RUNDOWN_DEFAULT_FONT_SIZE, RUNDOWN_LIMIT_FONT_SIZE } from '@go-shared/lib/property';

interface BrandFormDataBase {
  name: string;
  settings: RequiredBy<BrandSettings, 'notes'>;
  groupTypes: BrandGroupType[];
  dayThemes: BrandDayTheme[];
  imageTypes: BrandImageType[];
  fontSize: BrandFontSize;
  groupResouceTypes: BrandGroupType[];
}

type BrandFormData = BrandFormDataBase & {
  _dirty: boolean;
  _errors: Partial<BrandFormError>;
};

type BrandFormError = { [K in keyof BrandFormDataBase]: string };

function DEFAULT_FORM_DATA(brand?: Brand): BrandFormData {
  return {
    name: brand?.name ?? '',
    settings: {
      ...brand?.settings,
      notes: brand?.settings?.notes ?? [],
    },
    groupTypes: brand?.groupTypes ?? [],
    dayThemes: brand?.dayThemes ?? [],
    imageTypes: brand?.imageTypes ?? [],
    groupResouceTypes: brand?.groupResourceTypes ?? [],
    _dirty: false,
    _errors: {},
    fontSize: brand?.fontSize ?? {
      min: RUNDOWN_DEFAULT_FONT_SIZE.min,
      max: RUNDOWN_DEFAULT_FONT_SIZE.max,
    },
  };
}

interface BrandFormProps {
  edit: Brand;
  onClose?: () => void;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

export default function BrandForm(props: BrandFormProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<BrandFormData>(DEFAULT_FORM_DATA(props.edit));

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof BrandFormDataBase>(key: T, val: BrandFormDataBase[T]) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  async function submitForm() {
    setSaving(true);
    try {
      if (props.edit) {
        // user intends to edit an existing property
        await updateBrand(props.edit._id, {
          ...props.edit,
          name: formData.name,
          settings: formData.settings,
          groupTypes: formData.groupTypes,
          dayThemes: formData.dayThemes,
          imageTypes: formData.imageTypes,
          fontSize: formData.fontSize,
          groupResourceTypes: formData.groupResouceTypes,
        });
      }

      await props.onFormSubmitted();
      if (props.onClose) props.onClose();
    } finally {
      setSaving(false);
    }
  }

  const errored = Object.keys(formData._errors).length > 0;

  const files: VenueRequestFile[] = props.edit.logo
    ? [
        {
          _id: '123',
          name: lastEl(props.edit.logo.split('/')),
          location: props.edit.logo,
        },
      ]
    : [];

  async function onUploadFile(file: File) {
    await uploadBrandLogo(props.edit._id, file);
    await props.onFormSubmitted();
  }

  const notesOpts = formData.settings.notes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const groupTypeOpts = formData.groupTypes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const dayThemeOpts = formData.dayThemes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const imageTypesOpts = formData.imageTypes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const groupResouceTypeOpts = formData.groupResouceTypes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const formHtml = (
    <div className="grid grid-cols-3 gap-4">
      <BubbleFormGroup label="Name" error={formData._errors.name} className="col-span-3" asterisk>
        <BubbleInput
          block
          type="text"
          value={formData.name}
          onValue={val => changeForm('name', val)}
        />
      </BubbleFormGroup>
      <div className="col-span-3">
        <BubbleFileM label="Logo" files={files} onUploadFile={onUploadFile} />
        <div className="italic text-slate mt-1">
          The recommended specs: PNG of size 230x130 pixels for wide logos, 230x230 pixels for
          square logos
        </div>
      </div>

      <BubbleFormGroup
        label="Group Types"
        error={formData._errors.groupTypes}
        className="col-span-3"
      >
        <BubbleTypeahead
          block
          options={groupTypeOpts}
          placeholder="Enter group type name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={groupTypeOpts.length > 0}
          suggestionsText="No similar group types found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const groupTypeIdx = formData.groupTypes.findIndex(s => s._id === opt.item._id);
                  if (groupTypeIdx < 0) return;
                  const newGroupTypes = [...formData.groupTypes];
                  newGroupTypes[groupTypeIdx].name = val;
                  changeForm('groupTypes', newGroupTypes);
                }}
              />
            </div>
          )}
          onCreate={val => {
            const exists = formData.groupTypes.find(s => s.name === val);
            if (!exists) {
              changeForm('groupTypes', [
                ...formData.groupTypes,
                {
                  _id: new ObjectId().toHexString(),
                  name: val,
                },
              ]);
            }
          }}
          onTrash={val =>
            changeForm(
              'groupTypes',
              formData.groupTypes.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
      <BubbleFormGroup
        label="Image Types"
        error={formData._errors.imageTypes}
        className="col-span-3"
      >
        <BubbleTypeahead
          block
          options={imageTypesOpts}
          placeholder="Enter image type name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={imageTypesOpts.length > 0}
          suggestionsText="No similar image types found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const imageTypeIdx = formData.imageTypes.findIndex(s => s._id === opt.item._id);
                  if (imageTypeIdx < 0) return;
                  const newImageTypes = [...formData.imageTypes];
                  newImageTypes[imageTypeIdx].name = val;
                  changeForm('imageTypes', newImageTypes);
                }}
              />
            </div>
          )}
          onCreate={val => {
            const exists = formData.imageTypes.find(s => s.name === val);
            if (!exists) {
              changeForm('imageTypes', [
                ...formData.imageTypes,
                {
                  _id: new ObjectId().toHexString(),
                  name: val,
                },
              ]);
            }
          }}
          onTrash={val =>
            changeForm(
              'imageTypes',
              formData.imageTypes.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>

      <BubbleFormGroup label="Day Themes" error={formData._errors.dayThemes} className="col-span-3">
        <BubbleTypeahead
          block
          options={dayThemeOpts}
          placeholder="Enter day theme name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={dayThemeOpts.length > 0}
          suggestionsText="No similar day themes found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const dayThemeIdx = formData.dayThemes.findIndex(s => s._id === opt.item._id);
                  if (dayThemeIdx < 0) return;
                  const newDayThemes = [...formData.dayThemes];
                  newDayThemes[dayThemeIdx].name = val;
                  changeForm('dayThemes', newDayThemes);
                }}
              />
            </div>
          )}
          onCreate={val => {
            const exists = formData.dayThemes.find(s => s.name === val);
            if (!exists) {
              changeForm('dayThemes', [
                ...formData.dayThemes,
                {
                  _id: new ObjectId().toHexString(),
                  name: val,
                },
              ]);
            }
          }}
          onTrash={val =>
            changeForm(
              'dayThemes',
              formData.dayThemes.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
      <BubbleFormGroup
        label="Rundown Font Size"
        className="col-span-3"
        error={formData._errors.fontSize}
      >
        <div className="flex items-center">
          <div className="relative mr-3">
            <span>MIN </span>
            <BubbleInput
              min={RUNDOWN_LIMIT_FONT_SIZE.min}
              max={RUNDOWN_LIMIT_FONT_SIZE.max}
              type="number"
              className="mr-1"
              value={formData.fontSize.min}
              onValue={val => changeForm('fontSize', { min: val, max: formData.fontSize.max })}
            />
          </div>
          <div className="relative">
            <span>MAX </span>
            <BubbleInput
              min={RUNDOWN_LIMIT_FONT_SIZE.min}
              max={RUNDOWN_LIMIT_FONT_SIZE.max}
              type="number"
              className="mr-1"
              value={formData.fontSize.max}
              onValue={val => changeForm('fontSize', { min: formData.fontSize.min, max: val })}
            />
          </div>
        </div>
        <div className="italic text-slate mt-1">
          The limit values for Rundown are: MIN={RUNDOWN_LIMIT_FONT_SIZE.min} and MAX=
          {RUNDOWN_LIMIT_FONT_SIZE.max}
        </div>
      </BubbleFormGroup>

      <BubbleFormGroup label="Group Resource Type" className="col-span-3">
        <BubbleTypeahead
          block
          options={groupResouceTypeOpts}
          placeholder="Enter group resource type name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={groupResouceTypeOpts.length > 0}
          suggestionsText="No similar group resource types found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const resourceTypeIdx = formData.groupResouceTypes.findIndex(
                    s => s._id === opt.item._id
                  );
                  if (resourceTypeIdx < 0) return;
                  const newResourceGroupTypes = [...formData.groupResouceTypes];
                  newResourceGroupTypes.splice(resourceTypeIdx, 1, { ...opt.item, name: val });
                  changeForm('groupResouceTypes', newResourceGroupTypes);
                }}
              />
            </div>
          )}
          onCreate={val => {
            const exists = formData.groupResouceTypes.find(s => s.name === val);
            if (!exists) {
              changeForm('groupResouceTypes', [
                ...formData.groupResouceTypes,
                {
                  _id: new ObjectId().toHexString(),
                  name: val,
                },
              ]);
            }
          }}
          onTrash={val =>
            changeForm(
              'groupResouceTypes',
              formData.groupResouceTypes.filter(ar => ar._id !== val.item._id)
            )
          }
        />
        <div className="italic text-slate mt-1">
          Changing the Resource Types F&B, HSK, and ENT may affect the Group Export & Group Memo
        </div>
      </BubbleFormGroup>

      <BubbleFormGroup label="Additional Instance Details" className="col-span-3">
        <BubbleTypeahead
          block
          options={notesOpts}
          placeholder="Enter note name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={notesOpts.length > 0}
          suggestionsText="No similar notes found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <span className="text-xs font-semibold uppercase text-navy mx-3">Label</span>
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.label}
                className="flex-grow mr-3"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const nIdx = formData.settings.notes.findIndex(n => n._id === opt.item._id);
                  if (nIdx < 0) return;
                  const newNotes = [...formData.settings.notes];
                  newNotes[nIdx].label = val;
                  changeForm('settings', { ...formData.settings, notes: newNotes });
                }}
              />
              <span className="text-xs font-semibold uppercase text-navy mr-3">Value</span>
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.value}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const nIdx = formData.settings.notes.findIndex(n => n._id === opt.item._id);
                  if (nIdx < 0) return;
                  const newNotes = [...formData.settings.notes];
                  newNotes[nIdx].value = val;
                  changeForm('settings', { ...formData.settings, notes: newNotes });
                }}
              />
            </div>
          )}
          onCreate={val =>
            changeForm('settings', {
              ...formData.settings,
              notes: [
                ...formData.settings.notes,
                { _id: new ObjectId().toHexString(), label: val, value: '' },
              ],
            })
          }
          onTrash={val =>
            changeForm('settings', {
              ...formData.settings,
              notes: formData.settings.notes.filter(n => n._id !== val.item._id),
            })
          }
        />
      </BubbleFormGroup>
    </div>
  );

  const formActions = (
    <div className="flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || errored || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
